import React, { useState, useEffect } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2 } from 'lucide-react';
import Button from '../common/Button';
import AmbientBackground from '../common/AmbientBackground';
import { useReadingsStore } from '../../store/readingsStore';
import { generateReadingSummary } from '../../utils/readingUtils';
import { useAuthStore } from '../../store/authStore';
import { Link } from 'react-router-dom';

const ReadingHistoryList: React.FC = () => {
  const { readings, fetchReadings, deleteReading, isLoading, error } = useReadingsStore();
  const { user, initialized } = useAuthStore();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (initialized && user) {
      fetchReadings();
    }
  }, [fetchReadings, user, initialized]);

  // Add retry mechanism for loading failures
  useEffect(() => {
    if (error && retryCount < 3) {
      const timer = setTimeout(() => {
        console.log(`Retrying fetch readings (attempt ${retryCount + 1})...`);
        fetchReadings();
        setRetryCount(prev => prev + 1);
      }, 2000); // Retry after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [error, retryCount, fetchReadings]);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();

    if (confirmDelete === id) {
      await deleteReading(id);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(id);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const handleRetry = () => {
    setRetryCount(0);
    fetchReadings();
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return format(new Date(), 'MMMM d, yyyy');
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMMM d, yyyy') : format(new Date(), 'MMMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return format(new Date(), 'MMMM d, yyyy');
    }
  };

  const formatSpreadType = (spreadType?: string | null) => {
    if (!spreadType || typeof spreadType !== 'string') return 'Three Card Spread';
    return spreadType === 'virtual-daily-card'
      ? 'Daily Card Draw'
      : spreadType === 'two-options'
      ? 'Two Options'
      : spreadType === 'celtic-cross'
      ? 'Celtic Cross'
      : spreadType
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ') + ' Spread';
  };

  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sun"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-3xl mx-auto px-4"
      >
        <h1 className="font-display text-4xl md:text-5xl font-semibold text-ink dark:text-parchment mb-8">
          Your Reading Journal
        </h1>

        <div className="bg-vellum dark:bg-shadow/20 p-8 rounded-xl border border-mist dark:border-dusk text-center">
          <p className="font-display italic text-xl text-ink/70 dark:text-parchment/60 mb-6">
            Want to save and revisit your readings? Log in or sign up to create your tarot journal.
          </p>
          <div className="flex justify-center gap-4">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/login'}
            >
              Log In
            </Button>
            <Button
              variant="primary"
              onClick={() => window.location.href = '/signup'}
            >
              Sign Up
            </Button>
          </div>
        </div>

        <div className="mt-8 text-center space-x-4">
          <Link
            to="/guide/101"
            className="text-sm text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            Tarot 101
          </Link>
          <span className="text-mist">·</span>
          <Link
            to="/virtual-deck"
            className="text-sm text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            Virtual Deck
          </Link>
          <span className="text-mist">·</span>
          <Link
            to="/guide"
            className="text-sm text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            Tarot Guide
          </Link>
        </div>
      </motion.div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sun"></div>
        <p className="mt-4 text-ink/70 dark:text-parchment/60">Loading readings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex flex-col items-center py-12">
          <span className="text-red-500 text-4xl mb-4">!</span>
          <h2 className="font-display text-xl font-semibold text-ink dark:text-parchment mb-2">Error Loading Readings</h2>
          <p className="text-ink/70 dark:text-parchment/60 text-center mb-6">{error}</p>
          <Button variant="primary" onClick={handleRetry}>
            Try Again
          </Button>
          {retryCount > 0 && (
            <p className="mt-2 text-sm text-ink/50 dark:text-parchment/40">
              Retry attempt: {retryCount}/3
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <AmbientBackground mood="sun" backgroundImage="/bg-journal.png">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-4xl mx-auto px-4"
      >
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-ink dark:text-parchment">
            Your Reading Journal
          </h1>
          {readings.length > 0 && (
            <Link
              to="/insights"
              className="inline-flex items-center gap-1.5 text-sm text-sun dark:text-ember hover:opacity-80 transition-opacity"
            >
              <span>Reading Insights</span>
            </Link>
          )}
        </div>

        {/* Empty State */}
        {readings.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-center">
            <p className="font-display italic text-2xl text-ink/50 dark:text-parchment/40 mb-3">
              No readings yet
            </p>
            <p className="text-ink/60 dark:text-parchment/50 max-w-sm mb-8 leading-relaxed">
              Your journal is waiting. Each reading you save becomes part of your story — start your first one whenever you're ready.
            </p>
            <Button
              variant="primary"
              onClick={() => window.location.href = '/new-reading'}
            >
              Start a New Reading
            </Button>
          </div>
        ) : (
          /* Reading Entries */
          <div className="space-y-4">
            {readings.map((reading) => (
              <motion.div
                key={reading.id}
                layout
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className={`bg-vellum dark:bg-shadow rounded-xl border overflow-hidden transition-all ${
                  expandedId === reading.id
                    ? 'border-sun/30 dark:border-ember/30'
                    : 'border-mist dark:border-dusk hover:border-sun/50 dark:hover:border-ember/50'
                }`}
              >
                {/* Collapsed View */}
                <div
                  className="p-5 md:p-6 cursor-pointer"
                  onClick={() => toggleExpand(reading.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Reading Title */}
                      <h3 className="font-display text-xl md:text-2xl font-semibold text-ink dark:text-parchment leading-snug">
                        {generateReadingSummary(reading)}
                      </h3>

                      {/* Metadata Line */}
                      <p className="text-xs tracking-[0.08em] uppercase text-mist dark:text-mist mt-2">
                        {formatDate(reading.createdAt)} <span className="mx-1.5">·</span> {formatSpreadType(reading.spreadType)}
                        <span className="mx-1.5">·</span>
                        <span className="inline-flex items-center gap-1">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                            reading.is_virtual ? 'bg-sun dark:bg-ember' : 'bg-moon dark:bg-moon'
                          }`} />
                          {reading.is_virtual ? 'Virtual' : 'Physical'}
                        </span>
                      </p>

                      {/* Theme Tags */}
                      {reading.themes && reading.themes.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {reading.themes.map((theme, index) => (
                            <span
                              key={index}
                              className="bg-sun/10 text-sun dark:bg-ember/10 dark:text-ember px-2.5 py-0.5 rounded-full text-xs"
                            >
                              {theme.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex-shrink-0 mt-1">
                      <span className="text-mist text-lg">{expandedId === reading.id ? '−' : '+'}</span>
                    </div>
                  </div>
                </div>

                {/* Expanded View */}
                <AnimatePresence>
                  {expandedId === reading.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-mist dark:border-dusk"
                    >
                      <div className="p-5 md:p-6 space-y-6">
                        {/* Question */}
                        {reading.question && (
                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.05, duration: 0.25 }}
                            className="font-display italic text-base text-ink/80 dark:text-parchment/70 leading-relaxed"
                          >
                            "{reading.question}"
                          </motion.p>
                        )}

                        {/* Context */}
                        {reading.context && (
                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.25 }}
                            className="text-sm text-ink/70 dark:text-parchment/60 leading-relaxed"
                          >
                            {reading.context}
                          </motion.p>
                        )}

                        {/* Photo — hero treatment */}
                        {reading.imageUrl && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.15, duration: 0.25 }}
                          >
                            <button
                              onClick={() => setEnlargedImage(reading.imageUrl)}
                              className="block w-full group"
                            >
                              <img
                                src={reading.imageUrl}
                                alt="Tarot Spread"
                                className="w-full rounded-lg object-cover group-hover:opacity-90 transition-opacity"
                              />
                            </button>
                          </motion.div>
                        )}

                        {/* Cards — mini spread */}
                        {reading.cards && reading.cards.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.25 }}
                            className="grid grid-cols-2 md:grid-cols-3 gap-3"
                          >
                            {reading.cards.map((card, index) => (
                              <div
                                key={index}
                                className="p-3 rounded-lg bg-parchment/60 dark:bg-shadow/60"
                              >
                                <span className="text-xs tracking-[0.08em] uppercase text-mist block mb-1">
                                  {card.position}
                                </span>
                                <span className="font-display text-sm font-semibold text-ink dark:text-parchment">
                                  {card.name}
                                </span>
                                {card.isReversed && (
                                  <span className="text-sun dark:text-ember text-xs ml-1">(R)</span>
                                )}
                              </div>
                            ))}
                          </motion.div>
                        )}

                        {/* User Interpretation */}
                        {reading.userInterpretation && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.25, duration: 0.25 }}
                          >
                            <h4 className="text-xs tracking-[0.08em] uppercase text-mist mb-2">Your Interpretation</h4>
                            <p className="text-sm text-ink/80 dark:text-parchment/70 leading-relaxed">{reading.userInterpretation}</p>
                          </motion.div>
                        )}

                        {/* Enhanced Interpretation */}
                        {reading.aiInterpretation && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3, duration: 0.25 }}
                          >
                            <h4 className="text-xs tracking-[0.08em] uppercase text-mist mb-2">Enhanced Interpretation</h4>
                            <div className="space-y-2">
                              {reading.aiInterpretation.split('\n\n').map((paragraph, index) => (
                                <p key={index} className="text-sm text-ink/80 dark:text-parchment/70 leading-relaxed">
                                  {paragraph}
                                </p>
                              ))}
                            </div>
                          </motion.div>
                        )}

                        {/* Advice */}
                        {reading.advice && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.35, duration: 0.25 }}
                          >
                            <h4 className="text-xs tracking-[0.08em] uppercase text-mist mb-2">Advice</h4>
                            <div className="space-y-2">
                              {reading.advice.split('\n\n').map((paragraph, index) => (
                                <p key={index} className="text-sm text-ink/80 dark:text-parchment/70 leading-relaxed">
                                  {paragraph}
                                </p>
                              ))}
                            </div>
                          </motion.div>
                        )}

                        {/* Notes */}
                        {reading.notes && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.25 }}
                            className="bg-parchment/50 dark:bg-shadow/40 p-4 rounded-lg"
                          >
                            <h4 className="text-xs tracking-[0.08em] uppercase text-mist mb-2">Your Notes</h4>
                            <p className="text-sm text-ink/80 dark:text-parchment/70 leading-relaxed">{reading.notes}</p>
                          </motion.div>
                        )}

                        {/* Delete */}
                        <div className="pt-4 border-t border-mist dark:border-dusk">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={(e) => handleDelete(e, reading.id)}
                              className={`group relative p-2 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ${
                                confirmDelete === reading.id
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-red-500 dark:text-red-400'
                              }`}
                            >
                              <Trash2 size={20} />
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-ink text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                {confirmDelete === reading.id
                                  ? "Click again to release this reading's energy"
                                  : "Let this reading go? Click twice to confirm"}
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Enlarged Image Modal */}
      <AnimatePresence>
        {enlargedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setEnlargedImage(null)}
          >
            <div className="relative max-w-4xl w-full max-h-[90vh]">
              <img
                src={enlargedImage}
                alt="Enlarged Tarot Spread"
                className="w-full h-full object-contain rounded-lg"
              />
              <button
                onClick={() => setEnlargedImage(null)}
                className="absolute top-4 right-4 text-white hover:text-parchment/60 transition-colors"
              >
                <span className="text-2xl leading-none">×</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </AmbientBackground>
  );
};

export default ReadingHistoryList;